/**
 * Frontend error types and constants to match backend ErrorCode enum
 * to provide consistent error handling across the application
 */

export enum ErrorType {
  // HTTP Status Code Based Errors
  BAD_REQUEST = 'bad_request',
  UNAUTHORIZED = 'unauthorized',
  FORBIDDEN = 'forbidden',
  NOT_FOUND = 'not_found',
  CONFLICT = 'conflict',
  INTERNAL_SERVER_ERROR = 'internal_server_error',

  // Application Specific Errors (matching backend ErrorCode)
  INVALID = 'invalid',
  PERMISSION_DENIED = 'permission_denied',
  AUTHENTICATION_FAILED = 'authentication_failed',
  CSV_ERROR = 'csv_error',
  REPORT_IN_PROGRESS = 'report_in_progress',
  CANNOT_MARK_AS_COMPLETE = 'cannot_mark_as_complete',
  VALIDATION_ERROR = 'validation_error',
  TIMEOUT = 'timeout',

  // Frontend Specific Errors
  NETWORK_ERROR = 'network_error',
  UNKNOWN_ERROR = 'unknown_error',
}

export interface ErrorPageProps {
  errorType: ErrorType;
  statusCode?: number;
  message?: string;
  onRetry?: () => void;
  showRetry?: boolean;
}

export interface ApiError {
  status?: number;
  message: string;
  code?: string;
}

export interface ErrorSharingInfo {
  timestamp: string;
  errorType: ErrorType;
  errorMessage: string;
  statusCode?: number;
  resourceContext: Record<string, string>;
  pathname: string;
}

/**
 * Maps HTTP status codes to appropriate error types
 */
export const STATUS_CODE_TO_ERROR_TYPE: Record<number, ErrorType> = {
  400: ErrorType.BAD_REQUEST,
  401: ErrorType.UNAUTHORIZED,
  403: ErrorType.FORBIDDEN,
  404: ErrorType.NOT_FOUND,
  409: ErrorType.CONFLICT,
  500: ErrorType.INTERNAL_SERVER_ERROR,
  502: ErrorType.INTERNAL_SERVER_ERROR,
  503: ErrorType.INTERNAL_SERVER_ERROR,
  504: ErrorType.TIMEOUT,
};

/**
 * Maps backend error codes to frontend error types
 */
export const BACKEND_CODE_TO_ERROR_TYPE: Record<string, ErrorType> = {
  invalid: ErrorType.INVALID,
  permission_denied: ErrorType.PERMISSION_DENIED,
  authentication_failed: ErrorType.AUTHENTICATION_FAILED,
  csv_error: ErrorType.CSV_ERROR,
  report_in_progress: ErrorType.REPORT_IN_PROGRESS,
  cannot_mark_as_complete: ErrorType.CANNOT_MARK_AS_COMPLETE,
  validation_error: ErrorType.VALIDATION_ERROR,
  not_found: ErrorType.NOT_FOUND,
  timeout: ErrorType.TIMEOUT,
  conflict: ErrorType.CONFLICT,
  internal_error: ErrorType.INTERNAL_SERVER_ERROR,
};

/**
 * User-friendly error messages for each error type
 */
export const ERROR_MESSAGES: Record<ErrorType, { title: string; description: string }> = {
  [ErrorType.NOT_FOUND]: {
    title: 'Page Not Found',
    description: "The page you're looking for doesn't exist or has been moved.",
  },
  [ErrorType.INTERNAL_SERVER_ERROR]: {
    title: 'Something Went Wrong',
    description: "We're experiencing technical difficulties. Please try again later.",
  },
  [ErrorType.UNAUTHORIZED]: {
    title: 'Authentication Required',
    description: 'Please log in to access this page.',
  },
  [ErrorType.FORBIDDEN]: {
    title: 'Access Denied',
    description: "You don't have permission to access this resource.",
  },
  [ErrorType.BAD_REQUEST]: {
    title: 'Invalid Request',
    description: 'The request contains invalid data. Please check your input and try again.',
  },
  [ErrorType.CONFLICT]: {
    title: 'Resource Conflict',
    description: "The resource you're trying to modify has been changed by another user.",
  },
  [ErrorType.TIMEOUT]: {
    title: 'Request Timeout',
    description: 'The request took too long to complete. Please try again.',
  },
  [ErrorType.NETWORK_ERROR]: {
    title: 'Network Error',
    description: 'Unable to connect to the server. Please check your internet connection.',
  },
  [ErrorType.UNKNOWN_ERROR]: {
    title: 'Unexpected Error',
    description: 'An unexpected error occurred. Please try again or contact support.',
  },
  // Application specific errors
  [ErrorType.INVALID]: {
    title: 'Invalid Data',
    description: 'The provided data is invalid. Please check your input.',
  },
  [ErrorType.PERMISSION_DENIED]: {
    title: 'Permission Denied',
    description: "You don't have the required permissions for this action.",
  },
  [ErrorType.AUTHENTICATION_FAILED]: {
    title: 'Authentication Failed',
    description: 'Unable to authenticate your credentials. Please try again.',
  },
  [ErrorType.CSV_ERROR]: {
    title: 'CSV Export Error',
    description: 'There was an error processing the CSV file. Please try again.',
  },
  [ErrorType.REPORT_IN_PROGRESS]: {
    title: 'Report Generation In Progress',
    description: 'Your report is still being generated. Please wait a moment and try again.',
  },
  [ErrorType.CANNOT_MARK_AS_COMPLETE]: {
    title: 'Cannot Mark as Complete',
    description: 'This item cannot be marked as complete at this time.',
  },
  [ErrorType.VALIDATION_ERROR]: {
    title: 'Validation Error',
    description: 'Please correct the highlighted fields and try again.',
  },
};
